import { useState } from "react";
import { useMutation } from "react-query";
import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  List,
  Typography,
} from "@mui/material";
import { useFormik, FormikProps } from "formik";
import { object, string, ref } from "yup";
import ListFormItem from "@arelia-components/form/ListFormItem";
import PasswordInput from "@arelia-components/form/PasswordInput";
import { FormItem, ChangePasswordForm } from "@arelia-types/index";
import { http } from "src/api/http";
import theme from "src/theme";
import { toast } from "sonner";

export default function ChangePassword() {
  const [incorrectPassword, setIncorrectPassword] = useState(false);

  const { mutate } = useMutation(
    () =>
      http(`/api/account/password`, {
        method: "POST",
        body: {
          oldPassword: formik.values.currentPassword,
          newPassword: formik.values.newPassword,
        }
      }),
    {
      onSuccess: (response) => {
        setIncorrectPassword(!response.ok && response.status === 500);
        if (response.ok && response.status === 200)
          toast.success("Password was changed successfully!");
        formik.resetForm();
      },
    }
  );

  const formik: FormikProps<ChangePasswordForm> = useFormik<ChangePasswordForm>({
    initialValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
    validationSchema: object({
      currentPassword: string().required("Required"),
      newPassword: string()
        .required("Required")
        .min(6, "Must be at least 6 characters")
        .max(100, "Must not exceed 100 characters")
        .matches(
          /^(?=.*[0-9])(?=.*[a-z])(?=.*[A-Z])(?=.*[^a-zA-Z0-9]).+$/,
          "Must include at least one digit, one lowercase, one uppercase, and one non-alphanumeric character"
        ),
      confirmPassword: string()
        .oneOf([ref("newPassword")], "Passwords must match")
        .required("Required"),
    }),
    onSubmit: () => mutate(),
  });

  const changePasswordOptions: FormItem<ChangePasswordForm>[] = [
    { label: 'Current password', name: 'currentPassword' },
    { label: 'New password', name: 'newPassword' },
    { label: 'Confirm new password', name: 'confirmPassword' },
  ];

  return (
    <form onSubmit={formik.handleSubmit} style={{
      gridColumn: "span 8",
      gridRow: "span 6"
    }}>
      <Card sx={{ height: "100%" }}>
        <CardHeader title="Change Password" />
        <CardContent>
            <List>
              {changePasswordOptions.map(({ label, name }) => (
                <ListFormItem key={name} label={label}>
                  <PasswordInput
                    error={Boolean(formik.touched[name] && formik.errors[name])}
                    {...formik.getFieldProps(name)}
                    size="small"
                    fullWidth
                  />
                  {formik.touched[name] && formik.errors[name] && (
                    <Typography sx={{ color: theme.palette.error.main, marginTop: '10px' }}>
                      {formik.errors[name]}
                    </Typography>
                  )}
                </ListFormItem>
              ))}

              <Typography sx={{
                color: theme.palette.error.main,
                marginTop: "10px",
                display: incorrectPassword ? "block" : "none"
              }}>Incorrect Password</Typography>
            </List>
        </CardContent>

        <CardActions>
          <Button
            variant="contained"
            type="submit"
            disabled={!formik.isValid || formik.isSubmitting}
          >
            Submit
          </Button>
        </CardActions>
      </Card>
    </form>
  );
}
